/**
 * Phase 2b — Transport-specific lowerings of the unified resolver output.
 *
 * Every consumer of `ResolvedWorksheetActionState` calls through this module,
 * so behavior drift across transports becomes impossible to introduce by
 * accident. Adding a new transport means adding a new function here, not
 * re-interpreting the `ActionResult` shape.
 */
import type { SourceFilter } from '../query_builder/types'
import type { EffectiveFilter, ResolvedWorksheetActionState } from './resolveWorksheetActionState'

export type WorksheetRuntimeFilterPayload = {
  field_name: string
  value: any[]
  explicit_empty: boolean
  operator: 'include' | 'exclude'
  kind: 'dimension' | 'measure' | 'range'
  is_measure: boolean
}

const nonTupleFilters = (filters: readonly EffectiveFilter[]) =>
  filters.filter((f) => f.kind !== 'tuple') as Exclude<EffectiveFilter, { kind: 'tuple' }>[]

/**
 * Lowering used by the normalized worksheet request (the primary
 * production path used by extract-backed execution and dashboard tiles).
 */
export function toWorksheetRuntimeFilters(
  resolved: ResolvedWorksheetActionState,
): WorksheetRuntimeFilterPayload[] {
  return nonTupleFilters(resolved.filters).map((filter) => ({
    field_name: filter.fieldName,
    value: [...filter.values],
    explicit_empty: filter.explicitEmpty,
    operator: filter.operator,
    kind: filter.kind,
    is_measure: filter.isMeasure,
  }))
}

/**
 * Lowering used by the enhanced query builder (the simplified dashboard
 * action path that previously lived in `actionQueryIntegration.ts`). This
 * emits the legacy `SourceFilter` shape the enhanced builder expects while
 * carrying operator and measure info faithfully.
 */
export function toSourceFilters(resolved: ResolvedWorksheetActionState): SourceFilter[] {
  const out: SourceFilter[] = []
  for (const filter of nonTupleFilters(resolved.filters)) {
    if (filter.values.length === 0 && !filter.explicitEmpty) continue
    const op: SourceFilter['op'] = filter.explicitEmpty
      ? 'in_list'
      : filter.operator === 'exclude'
        ? filter.values.length === 1
          ? 'not_equals'
          : 'not_in_list'
        : filter.values.length === 1
          ? 'equals'
          : 'in_list'
    const valueString = filter.explicitEmpty
      ? ''
      : filter.values.length === 1
        ? String(filter.values[0])
        : filter.values.map((v) => String(v ?? '')).join(',')
    out.push({
      id: `action_filter_${filter.fieldName}`,
      field: filter.fieldName,
      op,
      value: valueString,
      filter_type: filter.isMeasure ? 'measure' : 'dimension',
      is_measure: filter.isMeasure,
    } as SourceFilter)
  }
  return out
}

/**
 * Lowering used by the oracle URL-params transport.
 * vf_<Field>=<csv>, parameters as <Name>=<value>.
 *
 * Note: the oracle transport cannot express every operator faithfully today
 * (e.g. exclude, tuple, range). For those cases we still emit the include-
 * list encoding and rely on the temporary post-filter shim to enforce the
 * rest of the semantics client-side. Phase 6 eliminates this gap by teaching
 * the transport the full model; until then this function captures exactly
 * what oracle can honor and the shim captures exactly what it cannot.
 */
export function toOracleQueryParams(
  resolved: ResolvedWorksheetActionState,
): Record<string, string> {
  const out: Record<string, string> = {}
  for (const [name, value] of Object.entries(resolved.parameters)) {
    const key = String(name || '').trim()
    if (!key) continue
    if (value === null || typeof value === 'undefined') continue
    out[key] = String(value)
  }
  for (const filter of nonTupleFilters(resolved.filters)) {
    if (filter.values.length === 0) continue
    const parts = filter.values.map((v) => String(v ?? '').trim()).filter(Boolean)
    if (parts.length === 0) continue
    out[`vf_${filter.fieldName}`] = parts.join(',')
  }
  return out
}

/**
 * Lowering used by the non-parity post-filter shim (oracle today).
 * Returns a predicate that matches the resolver's semantics closely enough
 * for UI responsiveness; query-time filtering remains the source of truth.
 */
export function buildPostFilterPredicate(
  resolved: ResolvedWorksheetActionState,
): (row: Record<string, any> | null | undefined) => boolean {
  const dimensionFilters = nonTupleFilters(resolved.filters)
  const tupleFilter = resolved.filters.find((f) => f.kind === 'tuple') as
    | (EffectiveFilter & { kind: 'tuple'; tuples: ReadonlyArray<Record<string, any>> })
    | undefined

  return (row) => {
    if (!row || typeof row !== 'object') return true
    for (const filter of dimensionFilters) {
      if (filter.values.length === 0 && !filter.explicitEmpty) continue
      const cell = (row as any)[filter.fieldName]
      if (typeof cell === 'undefined') continue
      if (filter.explicitEmpty) return false
      const hit = filter.values.some((v) => v === cell || String(v) === String(cell))
      if (filter.operator === 'exclude') {
        if (hit) return false
      } else {
        if (!hit) return false
      }
    }
    if (tupleFilter && tupleFilter.tuples.length > 0) {
      const matches = tupleFilter.tuples.some((tuple) => {
        const entries = Object.entries(tuple || {}).filter(([field]) => {
          const key = String(field || '').trim()
          return Boolean(key) && Object.prototype.hasOwnProperty.call(row, key)
        })
        if (entries.length === 0) return false
        return entries.every(([field, value]) => {
          const cell = (row as any)[field]
          return cell === value || String(cell) === String(value)
        })
      })
      if (tupleFilter.operator === 'exclude') {
        if (matches) return false
      } else {
        if (!matches) return false
      }
    }
    return true
  }
}
